// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

/**
 * Client-side image reduction for feedback attachments.
 *
 * Screenshots are resized in the browser before upload so the request stays
 * small even on slow mobile connections; the backend repeats the same
 * reduction defensively (a client is never trusted to enforce limits).
 *
 * Guard rules (mirrored from the server pipeline): never enlarge, pass
 * already-small images through untouched, and keep the original whenever the
 * re-encode ends up bigger.
 */

/** Maximum width/edge of a resized attachment. */
export const MAX_EDGE = 1280;
/** JPEG re-encode quality: small but legible. */
export const JPEG_QUALITY = 0.7;
/** Images below this size and within MAX_EDGE are not recompressed. */
export const PASS_THROUGH_BYTES = 500 * 1024;
/** Raw upload cap enforced in the UI (matches the server limit). */
export const MAX_FILE_BYTES = 2 * 1024 * 1024;
/** Maximum number of attachments. */
export const MAX_FILES = 3;

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export function isSupportedImage(file: File): boolean {
  return ACCEPTED_TYPES.includes(file.type);
}

/**
 * Attachment slots still free for user-picked files.
 *
 * The opt-in page screenshot is uploaded through the same `files` field and
 * counts against MAX_FILES, so it has to be budgeted here: forgetting it let
 * a user submit 4 attachments and get a 400 from the server.
 */
export function remainingAttachmentSlots(
  fileCount: number,
  screenshotAttached: boolean,
): number {
  return Math.max(0, MAX_FILES - fileCount - (screenshotAttached ? 1 : 0));
}

/**
 * Resize an image so its longest edge is at most MAX_EDGE and re-encode as
 * JPEG. Returns the input unchanged when resizing would not help.
 * Throws when the blob cannot be decoded as an image.
 */
export async function resizeImageForUpload(blob: Blob): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    throw new Error('The file could not be read as an image.');
  }

  try {
    const maxEdge = Math.max(bitmap.width, bitmap.height);
    const scale = Math.min(1, MAX_EDGE / maxEdge);

    if (scale === 1 && blob.size <= PASS_THROUGH_BYTES) {
      return blob;
    }

    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;

    // Flatten onto white so transparent PNGs do not turn black in JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);

    const encoded = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    );
    if (!encoded || encoded.size >= blob.size) {
      return blob;
    }
    return encoded;
  } finally {
    bitmap.close();
  }
}

/** File name with a `.jpg` extension for resized attachments. */
export function resizedName(name: string): string {
  const base = name.replace(/\.[^.]+$/, '');
  return `${base || 'attachment'}.jpg`;
}
